import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Zap } from "lucide-react";

const SHARE_TYPES = new Set(["lead_returned", "lead_opened", "lead_won", "lead_lost", "lead_sent"]);

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const seen = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => {
      setData(data);
      if (seen.current === null) {
        seen.current = new Set(data.items.map((i) => i.notif_id));
      } else {
        const fresh = data.items.filter((i) => !seen.current.has(i.notif_id));
        fresh.slice().reverse().forEach((n) => { toast(n.message); seen.current.add(n.notif_id); });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    load();
  };

  const openNotif = (n) => {
    if (!n.lead_id) return;
    setOpen(false);
    const focus = SHARE_TYPES.has(n.type) ? "&focus=history" : "";
    navigate(`/app/leads?lead=${n.lead_id}${focus}`);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && data.unread > 0) markAll(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notif-unread-count"
              className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
              {data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="border-b border-border px-4 py-3 font-heading text-sm font-semibold">Notifications</div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">No notifications yet.</p>
          ) : data.items.map((n) => (
            <button key={n.notif_id} type="button" onClick={() => openNotif(n)} data-testid={`notif-${n.notif_id}`}
              className={`flex w-full gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors ${n.lead_id ? "cursor-pointer hover:bg-accent" : "cursor-default"} ${n.read ? "" : "bg-primary/5"}`}>
              <Zap className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm leading-snug">{n.message}</p>
                <p className="mt-1 text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}{SHARE_TYPES.has(n.type) && n.lead_id ? " · View send history" : ""}</p>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
